import { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import { fetchMe, login as apiLogin, register as apiRegister, exchangeGoogleSession } from "@/lib/api";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // CRITICAL: If returning from Google OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and mint the JWT first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    const token = localStorage.getItem("gl_token");
    if (!token) {
      setLoading(false);
      return;
    }
    fetchMe()
      .then(setUser)
      .catch(() => localStorage.removeItem("gl_token"))
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const data = await apiLogin({ email, password });
    localStorage.setItem("gl_token", data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (name, email, password) => {
    const data = await apiRegister({ name, email, password });
    localStorage.setItem("gl_token", data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const loginWithGoogleSession = useCallback(async (sessionId) => {
    const data = await exchangeGoogleSession(sessionId);
    localStorage.setItem("gl_token", data.token);
    setUser(data.user);
    return data.user;
  }, []);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const startGoogleLogin = useCallback(() => {
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("gl_token");
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, register, logout, loginWithGoogleSession, startGoogleLogin, setUser }),
    [user, loading, login, register, logout, loginWithGoogleSession, startGoogleLogin]
  );
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
};

export const useAuth = () => useContext(AuthCtx);
